/**
 * API Key Management
 * Create and list Paean AI API keys for use with external tools like a8e
 */

import { getApiClient } from './client.js';

export interface ApiKeyInfo {
    id: number;
    key: string;
    name?: string;
    userId: number;
    enabled?: boolean;
    createdAt: string;
    updatedAt?: string;
}

/**
 * Create a new Paean AI API key
 */
export async function createApiKey(name?: string): Promise<{ success: boolean; data?: ApiKeyInfo; error?: string }> {
    try {
        const client = getApiClient();
        const response = await client.post('/apikey/create', { name });
        return {
            success: true,
            data: response.data.data,
        };
    } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to create API key';
        return { success: false, error: message };
    }
}

/**
 * List existing API keys for the current user
 */
export async function listApiKeys(skip = 0, take = 20): Promise<{ success: boolean; data?: ApiKeyInfo[]; error?: string }> {
    try {
        const client = getApiClient();
        const response = await client.get('/apikey/list', { params: { skip, take } });
        return {
            success: true,
            data: response.data.data,
        };
    } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to list API keys';
        return { success: false, error: message };
    }
}

/**
 * Get or create an API key specifically for a8e integration.
 * Looks for an existing key named "a8e-cli", creates one if not found.
 */
export async function getOrCreateA8eApiKey(): Promise<{ success: boolean; key?: string; error?: string }> {
    const listResult = await listApiKeys(0, 100);

    if (listResult.success && listResult.data) {
        const existing = listResult.data.find(
            k => k.name === 'a8e-cli' && k.enabled !== false,
        );
        if (existing) {
            return { success: true, key: existing.key };
        }
    }

    const createResult = await createApiKey('a8e-cli');
    if (createResult.success && createResult.data) {
        return { success: true, key: createResult.data.key };
    }

    return { success: false, error: createResult.error || 'Failed to provision API key' };
}
